<template>
    <div class="h-full flex flex-col overflow-auto scrollbar-gutter" @scroll="handleScrollStart" @scrollend="handleScrollEnd">
        <dev-multi-creator v-if="MultiCreateTransactionFeature.isEnabled()"/>
        <TransactionManager />
        <div class="min-h-[600px] grow flex flex-col min-w-[850px] p-2 pr-3 relative w-full">
            <div class="mb-2 flex justify-between">
                <div>
                    <UButton
                        title="Hurtigtaster ctrl,shift,b"
                        class="rounded-r-none"
                        size="sm"
                        color="neutral"
                        :variant="showLatestTransactionsList ? 'outline' : 'subtle'"
                        @click="showLatestTransactionsList = true"
                    >
                        Sist førte bilag
                    </UButton>
                    <UButton
                        title="Hurtigtaster ctrl,shift,k"
                        class="rounded-l-none"
                        size="sm"
                        color="neutral"
                        data-cy="drafts-button"
                        :variant="!showLatestTransactionsList ? 'outline' : 'subtle'"
                        @click="showLatestTransactionsList = false"
                    >
                        Kladder
                    </UButton>
                </div>
                <div class="flex gap-2">
                    <fulfillment-selector
                        v-if="fulfillment"
                        v-model="fulfillment.fulfillOnSuccess"
                        :assignment-no="fulfillment.assignmentNo"
                    />
                    <USwitch v-model="autoSaveDraft" label="Autolagre kladd" />
                </div>
            </div>
            <transaction-list
                v-if="showLatestTransactionsList"
                :transactions="transactions"
                sort-by-created-at
                :loading="loading"
            />
            <drafts-list v-if="!showLatestTransactionsList" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import {onMounted, onUnmounted, ref} from 'vue'
import TransactionList from '@/transaction-journal/TransactionList.vue'
import {findLatestTransactions, loading} from '@/transaction/transaction.service'
import {hasSelectedOrg, orgSelectionComplete} from '@/tenants/org.service'
import emitter, {useEmitter} from '@/services/infrastructure/emitter'
import {Transaction} from '@/types/transaction/Transaction'
import {MultiCreateTransactionFeature} from '@/transaction/create-transaction/feature-toggles'
import DevMultiCreator from '@/transaction/create-transaction/DevMultiCreator.vue'
import {DELETE_TRANSACTION, TRANSACTION_CREATED, TRANSACTION_UPDATED} from '@/types/transaction/TransactionEvent'
import {ORG_SELECTED, WINDOW_SCROLL_END, WINDOW_SCROLL_START} from '@/types/events'
import DraftsList from '@/draft/DraftsList.vue'
import hotkeys from 'hotkeys-js'
import {useTransactionDrafts} from '@/transaction-journal/transaction.draft.service'
import TransactionManager from '@/transaction/TransactionManager.vue'
import FulfillmentSelector from '@/assignments/FulfillmentSelector.vue'
import {useTransactionCreator} from '@/transaction/create-transaction/TransactionCreator.service'

const {onEvent, cleanUp} = useEmitter()
const {autoSaveDraft} = useTransactionDrafts()
const {fulfillment} = useTransactionCreator()

const showLatestTransactionsList = ref(true)

const transactions = ref<Array<Transaction>>([])

const fetchLatestTransactions = async () => transactions.value = await findLatestTransactions()

onMounted(async () => {
    hotkeys('ctrl+shift+b', () => showLatestTransactionsList.value = true)
    hotkeys('ctrl+shift+k', () => showLatestTransactionsList.value = false)

    await orgSelectionComplete
    if (!hasSelectedOrg()) {
        console.error('Accessing the accounting page without selected org. Should not be possible, this is a bug!')
    }
    await fetchLatestTransactions()

    onEvent(ORG_SELECTED, fetchLatestTransactions)
    onEvent(TRANSACTION_CREATED, fetchLatestTransactions)
    onEvent(TRANSACTION_UPDATED, fetchLatestTransactions)
    onEvent(DELETE_TRANSACTION, fetchLatestTransactions)
})

onUnmounted(cleanUp)

const handleScrollStart = () => {
    emitter.emit(WINDOW_SCROLL_START)
}

const handleScrollEnd = () => {
    emitter.emit(WINDOW_SCROLL_END)
}

</script>


<style scoped>
@reference "@/index.css";
@reference "tailwindcss";

.summary-line-grid {
    @apply grid px-2;
    grid-template-columns: 100px minmax(100px, 100%) 150px minmax(100px, 100%) 150px 150px 90px;
}

.label-grid {
    @apply grid px-2;
    grid-template-columns: 100px minmax(100px, 100%) 150px minmax(100px, 100%) 150px 240px;
}

.scrollbar-gutter {
    scrollbar-gutter: stable;
    scrollbar-width: thin;
}

</style>